import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import { CartFill, CheckCircleFill, InfoCircleFill } from "react-bootstrap-icons";
import { LocalDate } from "@js-joda/core";
import { toast } from "react-toastify";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import {
    inferIsAch,
    PaymentMethodBrandLabel,
} from "./Controls/PaymentMethodBrandLabel";
import { cents, showMoney } from "./Data/Money";
import { getInitials, avatarColor } from "./Data/Avatar";
import { SubscriptionParker } from "./Data/ApiTypes";
import { showDate } from "./Data/Date";
import { Api } from "./Data/Api";
import { parseSubscriptionParker } from "./Data/ApiParse";
import { PendingGroupParker } from "./GroupParkerAddModal";

const GRADIENT = "linear-gradient(135deg, #e8359a 0%, #c0116e 100%)";
const PINK = "#D61F84";

interface Props {
    show: boolean;
    onHide: () => void;
    facilityId: number;
    subscriptionId: number;
    isAccountsReceivable?: boolean;
    pending: PendingGroupParker[];
    paymentMethodLabel: string | null;
    canChargeSubscription: boolean;
    maxCreditCardAmountInCents?: number | null;
    /** When known, used with card limit checks. Otherwise inferred from paymentMethodLabel. */
    paymentMethodIsAch?: boolean;
    onSuccess: (parkers: SubscriptionParker[], options?: { refresh?: boolean }) => void;
}

type Step = "summary" | "confirm" | "success";

function PaymentMethodBox({ label, isAch }: { label: string; isAch: boolean }) {
    return (
        <div className="mb-3">
            <div className="fw-semibold mb-2" style={{ fontSize: "0.85rem" }}>Payment method</div>
            <div
                className="p-3 rounded-3 border"
                style={{ borderColor: PINK, background: "rgba(214,31,132,0.07)" }}
            >
                <PaymentMethodBrandLabel label={label} isAch={isAch} compact={false} />
                <div className="text-secondary" style={{ fontSize: "0.8rem", marginTop: 2 }}>
                    Enrolled on subscription
                </div>
            </div>
        </div>
    );
}

function AmountDueBanner({
    total,
    subtotal,
    parkerFees,
    label = "Amount Due",
    feesExcluded = false,
}: {
    total: number;
    subtotal: number;
    parkerFees: number;
    label?: string;
    feesExcluded?: boolean;
}) {
    const showBreakdown = !feesExcluded && parkerFees > 0 && subtotal > 0;
    return (
        <div
            className="rounded-3 px-3 py-3 mb-3"
            style={{ background: GRADIENT, color: "#fff" }}
        >
            <div className="d-flex align-items-start justify-content-between gap-3">
                <div className="d-flex align-items-center gap-2 min-w-0">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 36, height: 36, backgroundColor: "rgba(255,255,255,0.2)" }}
                    >
                        <CartFill size={18} />
                    </div>
                    <div className="min-w-0">
                        <div className="small text-uppercase fw-semibold" style={{ opacity: 0.85, letterSpacing: "0.04em" }}>
                            {label}
                        </div>
                        <div className="fw-bold fs-5">
                            {showMoney(cents(total))}
                            {feesExcluded ? "*" : ""}
                        </div>
                        {feesExcluded && (
                            <div style={{ fontSize: "0.7rem", lineHeight: 1.2, opacity: 0.75, marginTop: 1 }}>
                                *Does not include applicable fees
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showBreakdown && (
                <div className="mt-2 pt-2 border-top" style={{ borderColor: "rgba(255,255,255,0.25) !important", fontSize: "0.82rem" }}>
                    <div className="d-flex justify-content-between gap-2">
                        <span style={{ opacity: 0.9 }}>Parking prorate</span>
                        <span>{showMoney(cents(subtotal))}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-2 mt-1">
                        <span style={{ opacity: 0.9 }}>Fees</span>
                        <span>{showMoney(cents(parkerFees))}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function StepPill({ n, label, active }: { n: number; label: string; active: boolean }) {
    return (
        <span
            className={`d-flex align-items-center gap-1 ${active ? "fw-semibold" : "text-secondary"}`}
            style={active ? { color: PINK } : undefined}
        >
            <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{
                    width: 22,
                    height: 22,
                    fontSize: "0.75rem",
                    background: active ? PINK : "#e9ecef",
                    color: active ? "#fff" : "#6c757d",
                }}
            >
                {n}
            </span>
            {label}
        </span>
    );
}

function FinalizeStepper({ step, showConfirmStep }: { step: Step; showConfirmStep: boolean }) {
    if (!showConfirmStep) {
        return (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.8rem" }}>
                <StepPill n={1} label="Review" active={step === "summary"} />
            </div>
        );
    }
    return (
        <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.8rem" }}>
            <StepPill n={1} label="Review" active={step === "summary"} />
            <span className="text-secondary">→</span>
            <StepPill n={2} label="Confirm" active={step === "confirm"} />
        </div>
    );
}

function FinalizeSuccessStep({
    parkerCount,
    paidAmount,
    charged,
    onDone,
}: {
    parkerCount: number;
    paidAmount: number;
    charged: boolean;
    onDone: () => void;
}) {
    const label = parkerCount === 1 ? "parker" : "parkers";
    return (
        <div className="d-flex flex-column align-items-center text-center p-4 gap-3">
            <CheckCircleFill size={48} style={{ color: PINK }} />
            <div>
                <div className="fw-semibold fs-5 mb-1">
                    {charged ? "Payment successful!" : "Parkers added!"}
                </div>
                <div className="text-secondary" style={{ fontSize: "0.9rem" }}>
                    {charged
                        ? `${showMoney(cents(paidAmount))} charged. ${parkerCount} ${label} added to the plan.`
                        : `No charge today. ${parkerCount} ${label} added to the plan.`}
                </div>
            </div>
            <Button className="btn-main px-5 mt-1" size="sm" onClick={onDone}>
                Done
            </Button>
        </div>
    );
}

export function GroupParkerFinalizeModal({
    show,
    onHide,
    facilityId,
    subscriptionId,
    isAccountsReceivable = false,
    pending,
    paymentMethodLabel,
    canChargeSubscription,
    maxCreditCardAmountInCents = null,
    paymentMethodIsAch,
    onSuccess,
}: Props) {
    const [step, setStep] = useState<Step>("summary");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | undefined>();
    const [fulfilledParkers, setFulfilledParkers] = useState<SubscriptionParker[]>([]);
    const [paidAmount, setPaidAmount] = useState(0);
    const [willAmendOpenInvoice, setWillAmendOpenInvoice] = useState(false);
    const [quotePrice, setQuotePrice] = useState(0);
    const [quoteSubtotal, setQuoteSubtotal] = useState(0);
    const [quoteParkerFees, setQuoteParkerFees] = useState(0);
    const prevShowRef = useRef(false);
    const pendingSuccessRef = useRef<SubscriptionParker[] | "refresh" | null>(null);

    const estimatedSubtotal = useMemo(
        () => pending.reduce((sum, p) => sum + (p.previewSubtotal ?? p.previewPrice ?? 0), 0),
        [pending]
    );
    const showConfirmStep = !isAccountsReceivable && estimatedSubtotal > 0;
    const needsPayment = showConfirmStep;
    const isAch = inferIsAch(paymentMethodLabel, paymentMethodIsAch);

    const amountForCardLimit = step === "confirm" && quotePrice > 0 ? quotePrice : estimatedSubtotal;
    const creditCardLimitExceeded = maxCreditCardAmountInCents != null
        && amountForCardLimit > maxCreditCardAmountInCents;
    const cardLimitBlocked = creditCardLimitExceeded && !isAch;
    const permissionBlocked = needsPayment && !canChargeSubscription;
    const confirmBlocked = permissionBlocked || cardLimitBlocked;

    const parkersBody = () => ({
        parkers: pending.map(p => ({
            name: p.name,
            emailAddress: p.emailAddress,
            phoneNumber: p.phoneNumber || null,
            startDate: p.startDate,
            subscriptionGroupId: p.subscriptionGroupId ?? null,
        })),
    });

    const resetState = () => {
        setStep("summary");
        setFulfilledParkers([]);
        setPaidAmount(0);
        setWillAmendOpenInvoice(false);
        setQuotePrice(0);
        setQuoteSubtotal(0);
        setQuoteParkerFees(0);
        setErr(undefined);
        setSubmitting(false);
    };

    useEffect(() => {
        if (show && !prevShowRef.current) {
            pendingSuccessRef.current = null;
            resetState();
        }
        prevShowRef.current = show;
    }, [show]);

    useEffect(() => {
        if (!show || !isAccountsReceivable || pending.length === 0) return;
        let cancelled = false;
        const startDate = pending[0].startDate;
        Api.groupParkerPreview(facilityId, subscriptionId, {
            startDate,
            pendingAssignCount: 0,
            pendingSameGroupAssignCount: 0,
        })
            .then(r => {
                if (!cancelled && r.isOk) setWillAmendOpenInvoice(!!r.value.willAmendOpenInvoice);
            })
            .catch(() => {
                if (!cancelled) setWillAmendOpenInvoice(false);
            });
        return () => { cancelled = true; };
    }, [show, isAccountsReceivable, facilityId, subscriptionId, pending]);

    const busy = submitting;

    const handleClose = () => {
        if (busy) return;
        pendingSuccessRef.current = null;
        onHide();
    };

    const handleDone = () => {
        if (!pendingSuccessRef.current) {
            pendingSuccessRef.current = fulfilledParkers.length > 0 ? fulfilledParkers : "refresh";
        }
        onHide();
    };

    const handleExited = () => {
        const pendingSuccess = pendingSuccessRef.current;
        pendingSuccessRef.current = null;
        if (Array.isArray(pendingSuccess)) {
            onSuccess(pendingSuccess);
        } else if (pendingSuccess === "refresh") {
            onSuccess([], { refresh: true });
        }
        resetState();
    };

    const handleContinueToConfirm = async () => {
        if (confirmBlocked) {
            if (permissionBlocked) {
                setErr("You do not have permission to charge this subscription. Contact an administrator or clear the cart.");
            } else if (cardLimitBlocked) {
                setErr("This amount exceeds the facility card limit. The enrolled payment method must be a bank account (ACH), or contact the facility.");
            }
            return;
        }

        setSubmitting(true);
        setErr(undefined);
        try {
            const resp = await Api.groupParkerFinalizeQuote(facilityId, subscriptionId, parkersBody());
            if (!resp.isOk) {
                toast.error(resp.error ?? "Could not load fee quote. Please try again.");
                return;
            }
            setQuotePrice(resp.value.price ?? 0);
            setQuoteSubtotal(resp.value.subtotal ?? 0);
            setQuoteParkerFees(resp.value.parkerFees ?? 0);
            setStep("confirm");
        } catch {
            toast.error("Could not load fee quote. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleFinalize = async () => {
        if (confirmBlocked) {
            if (permissionBlocked) {
                setErr("You do not have permission to charge this subscription. Contact an administrator or clear the cart.");
            } else if (cardLimitBlocked) {
                setErr("This amount exceeds the facility card limit. The enrolled payment method must be a bank account (ACH), or contact the facility.");
            }
            return;
        }

        setSubmitting(true);
        setErr(undefined);
        try {
            const resp = await Api.groupParkerFinalize(facilityId, subscriptionId, parkersBody());
            if (!resp.isOk) {
                toast.error(resp.error ?? "Finalize failed. Please try again.");
                return;
            }
            const parkers = (resp.value.parkers ?? []).map((p: unknown) => parseSubscriptionParker(p as any));
            setFulfilledParkers(parkers);
            setPaidAmount(resp.value.price ?? (quotePrice || estimatedSubtotal));
            setStep("success");
        } catch {
            toast.error("Finalize failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const completedParkerCount = fulfilledParkers.length || pending.length;
    const headerTitle = step === "confirm" ? "Confirm" : "Review";

    return (
        <Modal
            show={show}
            onHide={step === "success" ? handleDone : handleClose}
            onExited={handleExited}
            backdrop="static"
            keyboard={!busy}
            centered
            size="lg"
            fullscreen="sm-down"
        >
            {step !== "success" && (
            <Modal.Header closeButton={!busy} className="border-0 pb-0 px-3 pt-3 align-items-start">
                <div className="d-flex flex-column gap-1 flex-grow-1 me-2">
                    <FinalizeStepper step={step} showConfirmStep={showConfirmStep} />
                    <div className="mt-1">
                        <HeaderText size={6}>{headerTitle}</HeaderText>
                        <div className="text-secondary mt-1" style={{ fontSize: "0.82rem" }}>
                            {pending.length} parker{pending.length !== 1 ? "s" : ""} ready to add
                        </div>
                    </div>
                </div>
            </Modal.Header>
            )}

            {step === "summary" && (
                <>
                    <Modal.Body className="pt-2 px-3">
                        <AmountDueBanner
                            total={estimatedSubtotal}
                            subtotal={estimatedSubtotal}
                            parkerFees={0}
                            feesExcluded={!isAccountsReceivable && estimatedSubtotal > 0}
                            label={isAccountsReceivable ? "Applied to Invoice" : "Due Today"}
                        />

                        {needsPayment && paymentMethodLabel && (
                            <PaymentMethodBox label={paymentMethodLabel} isAch={isAch} />
                        )}

                        <div className="d-flex flex-column gap-2 mb-3">
                            {pending.map(p => (
                                <div
                                    key={p.key}
                                    className="border rounded-3 px-3 py-2 bg-white d-flex align-items-center gap-2 gap-sm-3"
                                    style={{
                                        background: "rgba(214,31,132,0.04)",
                                        borderColor: "rgba(214,31,132,0.25)",
                                        minWidth: 0,
                                    }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            backgroundColor: avatarColor(p.name),
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {getInitials(p.name)}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-truncate">{p.name}</div>
                                        {p.emailAddress && (
                                            <div
                                                className="text-muted text-truncate"
                                                style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}
                                            >
                                                {p.emailAddress}
                                            </div>
                                        )}
                                        <div
                                            className="text-muted text-truncate"
                                            style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}
                                        >
                                            Start {showDate(LocalDate.parse(p.startDate))}
                                            {!p.isCurrentPeriod
                                                && (p.previewSubtotal ?? p.previewPrice ?? 0) === 0
                                                && " · billed next period"}
                                        </div>
                                    </div>
                                    <div className="fw-semibold flex-shrink-0" style={{ fontSize: "0.85rem" }}>
                                        {(p.previewSubtotal ?? p.previewPrice ?? 0) > 0
                                            ? showMoney(cents(p.previewSubtotal ?? p.previewPrice ?? 0))
                                            : "—"}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {err && <Alert variant="danger" className="py-2 small">{err}</Alert>}

                        {permissionBlocked && (
                            <Alert variant="warning" className="py-2 small mb-3">
                                You do not have permission to charge this subscription. Finalize is disabled until you have Charge Subscription Payment access or the cart is cleared.
                            </Alert>
                        )}

                        {cardLimitBlocked && (
                            <Alert variant="warning" className="py-2 small mb-3">
                                This amount exceeds the facility credit card limit. Use a bank account (ACH) on the subscription, or contact the facility.
                            </Alert>
                        )}

                        {!confirmBlocked && (
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>
                                    {showConfirmStep
                                        ? "Click Continue to review fees before charging."
                                        : isAccountsReceivable
                                            ? willAmendOpenInvoice
                                                ? "No charge today. An amended invoice will reflect the updated charges."
                                                : "No charge today. Changes will appear on the next invoice."
                                            : "No charge today. Parkers will be added to the plan."}
                                </span>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 px-3 pb-3">
                        <Button size="sm" className="px-4" onClick={handleClose} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            className="btn-main px-4"
                            onClick={showConfirmStep ? handleContinueToConfirm : handleFinalize}
                            loading={submitting}
                            loadingText={showConfirmStep ? "Loading…" : "Finalizing…"}
                            disabled={confirmBlocked}
                        >
                            {showConfirmStep ? "Continue" : "Confirm"}
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === "confirm" && (
                <>
                    <Modal.Body className="pt-2 px-3">
                        <AmountDueBanner
                            total={quotePrice}
                            subtotal={quoteSubtotal}
                            parkerFees={quoteParkerFees}
                            feesExcluded={false}
                            label="Amount Due"
                        />

                        {paymentMethodLabel && (
                            <PaymentMethodBox label={paymentMethodLabel} isAch={isAch} />
                        )}

                        {err && <Alert variant="danger" className="py-2 small">{err}</Alert>}

                        {permissionBlocked && (
                            <Alert variant="warning" className="py-2 small mb-3">
                                You do not have permission to charge this subscription. Finalize is disabled until you have Charge Subscription Payment access or the cart is cleared.
                            </Alert>
                        )}

                        {cardLimitBlocked && (
                            <Alert variant="warning" className="py-2 small mb-3">
                                This amount exceeds the facility credit card limit. Use a bank account (ACH) on the subscription, or contact the facility.
                            </Alert>
                        )}

                        {!confirmBlocked && (
                            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                                <InfoCircleFill className="flex-shrink-0 mt-1" />
                                <span>
                                    Click Charge Account Now to charge the enrolled payment method and add parkers.
                                </span>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 px-3 pb-3 gap-2 w-100">
                        <Button
                            size="sm"
                            className="flex-fill flex-sm-grow-0 px-4"
                            onClick={() => { setErr(undefined); setStep("summary"); }}
                            disabled={submitting}
                        >
                            ← Back
                        </Button>
                        <Button
                            size="sm"
                            className="btn-main flex-fill flex-sm-grow-0 px-4"
                            onClick={handleFinalize}
                            loading={submitting}
                            loadingText="Charging…"
                            disabled={confirmBlocked}
                        >
                            Charge Account Now
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {step === "success" && (
                <FinalizeSuccessStep
                    parkerCount={completedParkerCount}
                    paidAmount={paidAmount}
                    charged={needsPayment}
                    onDone={handleDone}
                />
            )}
        </Modal>
    );
}
